import { motion } from 'framer-motion'
import { Bot, Code2, Sparkles, Zap } from 'lucide-react'
import Slide from '../components/Slide'
import TypingAnimation from '../components/Animations/TypingAnimation'

interface SlideProps {
  isActive: boolean
}

export default function TitleSlide({ isActive: _isActive }: SlideProps) {
  return (
    <Slide className="items-center justify-center text-center">
      {/* Floating decorative elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0],
          }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-[15%] left-[15%]"
        >
          <Code2 className="w-16 h-16 text-electric-cyan/30" />
        </motion.div>
        <motion.div
          animate={{
            y: [0, 15, 0],
            rotate: [0, -15, 15, 0],
          }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          className="absolute top-[20%] right-[20%]"
        >
          <Bot className="w-20 h-20 text-electric-purple/30" />
        </motion.div>
        <motion.div
          animate={{
            y: [0, -25, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
          className="absolute bottom-[25%] left-[20%]"
        >
          <Sparkles className="w-14 h-14 text-electric-magenta/30" />
        </motion.div>
        <motion.div
          animate={{
            y: [0, 20, 0],
            x: [0, -10, 0],
          }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          className="absolute bottom-[20%] right-[15%]"
        >
          <Zap className="w-12 h-12 text-electric-orange/30" />
        </motion.div>
      </div>

      {/* Main content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6">
        {/* Pre-title badge */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="mb-8"
        >
          <span className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full text-sm font-medium text-electric-cyan">
            <Sparkles className="w-4 h-4" />
            The New Standard in Development
          </span>
        </motion.div>

        {/* Main title with gradient */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="font-display text-6xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight"
        >
          <span className="text-gradient">AI Coding</span>
          <br />
          <span className="text-white">Workflows</span>
        </motion.h1>

        {/* Subtitle with typing effect */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="text-xl md:text-2xl text-white/60 mb-12 font-light h-8"
        >
          <TypingAnimation
            text="Transforming how developers build software"
            speed={40}
            delay={1200}
          />
        </motion.div>

        {/* Animated stats row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.5 }}
          className="flex flex-wrap justify-center gap-8 md:gap-16"
        >
          {[
            { value: '55%', label: 'Faster Task Completion' },
            { value: '73%', label: 'Stay in Flow' },
            { value: '70%', label: 'Mental Effort Saved' },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.7 + index * 0.15, duration: 0.4 }}
              className="text-center"
            >
              <div className="text-3xl md:text-4xl font-bold text-gradient mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-white/50">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Source footnote */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 0.5 }}
          className="mt-4 text-xs text-white/30"
        >
          * GitHub Copilot Research, ACM 2024
        </motion.p>

      </div>
    </Slide>
  )
}
